import React, { useState, useMemo, useEffect } from 'react';

/*
  Shared pagination used across admin + dashboard.

  Server-side (backend returns page/pages/total):
    <Pagination page={p} pages={pages} total={total} perPage={20} onPage={setPage} />

  Client-side (you have the full array in memory):
    const { pageItems, page, pages, total, perPage, setPage } = usePagination(rows, 10);
    ...render pageItems...
    <Pagination page={page} pages={pages} total={total} perPage={perPage} onPage={setPage} />
*/

// Compact page list with ellipses: 1 … 4 5 [6] 7 8 … 20
function pageList(page, pages) {
  const out = [];
  let last = 0;
  for (let n = 1; n <= pages; n++) {
    if (n === 1 || n === pages || (n >= page - 1 && n <= page + 1)) {
      if (last && n - last > 1) out.push('…');
      out.push(n);
      last = n;
    }
  }
  return out;
}

export function usePagination(items, perPage = 10) {
  const [page, setPage] = useState(1);
  const total = Array.isArray(items) ? items.length : 0;
  const pages = Math.max(1, Math.ceil(total / perPage));

  useEffect(() => { if (page > pages) setPage(1); }, [pages, page]);

  const pageItems = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];
    const start = (page - 1) * perPage;
    return arr.slice(start, start + perPage);
  }, [items, page, perPage]);

  return { page, setPage, pages, total, perPage, pageItems };
}

const Pagination = ({ page = 1, pages = 1, total, perPage, onPage }) => {
  if (!pages || pages <= 1) return null;

  const go = (n) => {
    const p = Math.max(1, Math.min(pages, n));
    if (p !== page && onPage) onPage(p);
  };

  let info;
  if (total != null && perPage) {
    const start = total === 0 ? 0 : (page - 1) * perPage + 1;
    const end = Math.min(total, page * perPage);
    info = `Showing ${start}–${end} of ${total}`;
  } else if (total != null) {
    info = `${total} item${total === 1 ? '' : 's'}`;
  } else {
    info = `Page ${page} of ${pages}`;
  }

  return (
    <div className="rp-pg">
      <span className="rp-pg-info">{info}</span>
      <div className="rp-pg-ctrls">
        <button className="rp-pg-btn" disabled={page <= 1} onClick={() => go(1)} aria-label="First page">«</button>
        <button className="rp-pg-btn" disabled={page <= 1} onClick={() => go(page - 1)} aria-label="Previous page">‹</button>
        {pageList(page, pages).map((it, i) =>
          it === '…'
            ? <span key={`dots-${i}`} className="rp-pg-dots">…</span>
            : <button key={it} className={`rp-pg-num ${it === page ? 'active' : ''}`} onClick={() => go(it)}>{it}</button>
        )}
        <button className="rp-pg-btn" disabled={page >= pages} onClick={() => go(page + 1)} aria-label="Next page">›</button>
        <button className="rp-pg-btn" disabled={page >= pages} onClick={() => go(pages)} aria-label="Last page">»</button>
      </div>
    </div>
  );
};

export default Pagination;
